import { create } from "zustand";
import { persist } from "zustand/middleware";

export type DocumentFarmStatus =
  /** Switched off by the person using this machine. */
  | "off"
  /** On, but with nothing left to read right now. */
  | "idle"
  /** Reading a document. */
  | "working"
  /** The last attempt failed — a backlog fetch, or every document in a row — and a retry is scheduled. */
  | "retrying";

interface DocumentFarmStore {
  /**
   * Whether this machine contributes to the backlog sweep at all.
   *
   * Persisted, so someone who turned it off during a call does not silently get it back on the next
   * reload. On by default: the whole point is that idle desktop capacity is used without anyone
   * having to opt in.
   */
  enabled: boolean;
  status: DocumentFarmStatus;
  /** Documents this machine still holds a claim on and has not read yet. */
  remaining: number;
  /** Documents finished since the app launched. Not persisted — it describes this run. */
  completedThisSession: number;
  /** Documents that would not read since the app launched, and were skipped. */
  failedThisSession: number;
  /** Filename of the document being read, for the status line. */
  currentDocumentName?: string;
  /**
   * A broker's statements from fraction.com this machine is reading right now — see
   * `publicStatementReads`. Counts only: the files are a stranger's, so no name is ever shown.
   */
  publicStatementRead?: { filesRead: number; filesTotal: number };
  /** fraction.com readings finished since the app launched. */
  publicStatementReadsThisSession: number;
  setEnabled: (enabled: boolean) => void;
  setStatus: (status: DocumentFarmStatus) => void;
  /** One document started; `undefined` when the sweep goes back to waiting. */
  setCurrentDocument: (name: string | undefined) => void;
  setRemaining: (remaining: number) => void;
  recordCompleted: () => void;
  recordFailed: () => void;
  setPublicStatementRead: (progress: { filesRead: number; filesTotal: number } | undefined) => void;
  recordPublicStatementRead: () => void;
}

/**
 * Progress and the on/off switch for the desktop document-processing farm.
 *
 * The sweep itself lives in `components/agent/DocumentProcessingFarm.tsx`; this is only what the
 * account dropdown renders and what that dropdown's switch writes. Deliberately a store rather than
 * component state, because the two live in different subtrees.
 */
export const useDocumentFarmStore = create<DocumentFarmStore>()(
  persist(
    (set) => ({
      enabled: true,
      status: "idle",
      remaining: 0,
      completedThisSession: 0,
      failedThisSession: 0,
      currentDocumentName: undefined,
      publicStatementRead: undefined,
      publicStatementReadsThisSession: 0,
      setEnabled: (enabled) =>
        set(
          enabled
            ? { enabled, status: "idle" }
            : {
                enabled,
                status: "off",
                remaining: 0,
                currentDocumentName: undefined,
                publicStatementRead: undefined,
              }
        ),
      setStatus: (status) => set({ status }),
      setCurrentDocument: (currentDocumentName) => set({ currentDocumentName }),
      setRemaining: (remaining) => set({ remaining }),
      recordCompleted: () => set((state) => ({ completedThisSession: state.completedThisSession + 1 })),
      recordFailed: () => set((state) => ({ failedThisSession: state.failedThisSession + 1 })),
      setPublicStatementRead: (publicStatementRead) => set({ publicStatementRead }),
      recordPublicStatementRead: () =>
        set((state) => ({ publicStatementReadsThisSession: state.publicStatementReadsThisSession + 1 })),
    }),
    {
      name: "document-farm",
      // Only the switch survives a reload. Everything else describes work in flight on this run,
      // and restoring a stale "working, 12 remaining" would show progress that nothing is making.
      partialize: (state) => ({ enabled: state.enabled }),
    }
  )
);
